import { create } from 'zustand';
import type { PersonaOut, CritiqueJobOut } from '../types';

export type CritiqueRunStatus = 'pending' | 'running' | 'completed' | 'failed';

/** Dropdown value representing "critique the entire document". */
export const WHOLE_DOCUMENT_SCOPE = '__whole_document__';

/** Dropdown value representing "critique the current Word selection". */
export const SELECTED_TEXT_SCOPE = '__selected_text__';

/** Section title sent to the critic API for highlighted text scope. */
export const SELECTED_PASSAGE_TITLE = 'Selected passage';

export interface CritiqueRun {
  personaId: string;
  personaName: string;
  status: CritiqueRunStatus;
  job: CritiqueJobOut | null;
  error: string | null;
}

interface CriticState {
  personas: PersonaOut[];
  isLoadingPersonas: boolean;
  personasError: string | null;
  /** Manage tab: which persona row is expanded. */
  selectedPersonaId: string | null;
  /** Critic tab: personas selected for critique. */
  selectedPersonaIds: string[];
  /** Resolved preview of the text that will be sent for the current scope. */
  selectedText: string | null;
  /** Critic tab: which scope to critique — WHOLE_DOCUMENT_SCOPE or a content control title. */
  criticScope: string;
  /** Titled content controls available in the document, for the scope dropdown. */
  contentControlTitles: string[];
  critiquing: boolean;
  critiqueRuns: CritiqueRun[];

  setPersonas: (personas: PersonaOut[]) => void;
  addPersona: (persona: PersonaOut) => void;
  updatePersonaInList: (persona: PersonaOut) => void;
  removePersona: (personaId: string) => void;
  setIsLoadingPersonas: (loading: boolean) => void;
  setPersonasError: (error: string | null) => void;
  setSelectedPersonaId: (id: string | null) => void;
  setSelectedPersonaIds: (ids: string[]) => void;
  togglePersonaSelection: (personaId: string) => void;
  setSelectedText: (text: string | null) => void;
  setCriticScope: (scope: string) => void;
  setContentControlTitles: (titles: string[]) => void;
  setCritiquing: (critiquing: boolean) => void;
  setCritiqueRuns: (runs: CritiqueRun[]) => void;
  updateCritiqueRun: (
    personaId: string,
    patch: Partial<Omit<CritiqueRun, 'personaId'>>,
  ) => void;
  reset: () => void;
}

export const useCriticStore = create<CriticState>((set) => ({
  personas: [],
  isLoadingPersonas: false,
  personasError: null,
  selectedPersonaId: null,
  selectedPersonaIds: [],
  selectedText: null,
  criticScope: WHOLE_DOCUMENT_SCOPE,
  contentControlTitles: [],
  critiquing: false,
  critiqueRuns: [],

  setPersonas: (personas) => set({ personas }),
  addPersona: (persona) =>
    set((s) => ({ personas: [persona, ...s.personas] })),
  updatePersonaInList: (persona) =>
    set((s) => ({
      personas: s.personas.map((p) => (p.id === persona.id ? persona : p)),
      critiqueRuns: s.critiqueRuns.map((run) =>
        run.personaId === persona.id
          ? { ...run, personaName: persona.name }
          : run,
      ),
    })),
  removePersona: (personaId) =>
    set((s) => ({
      personas: s.personas.filter((p) => p.id !== personaId),
      selectedPersonaId:
        s.selectedPersonaId === personaId ? null : s.selectedPersonaId,
      selectedPersonaIds: s.selectedPersonaIds.filter((id) => id !== personaId),
      critiqueRuns: s.critiqueRuns.filter((run) => run.personaId !== personaId),
    })),
  setIsLoadingPersonas: (loading) => set({ isLoadingPersonas: loading }),
  setPersonasError: (error) => set({ personasError: error }),
  setSelectedPersonaId: (id) => set({ selectedPersonaId: id }),
  setSelectedPersonaIds: (ids) => set({ selectedPersonaIds: ids }),
  togglePersonaSelection: (personaId) =>
    set((s) => ({
      selectedPersonaIds: s.selectedPersonaIds.includes(personaId)
        ? s.selectedPersonaIds.filter((id) => id !== personaId)
        : [...s.selectedPersonaIds, personaId],
    })),
  setSelectedText: (text) => set({ selectedText: text }),
  setCriticScope: (scope) => set({ criticScope: scope }),
  setContentControlTitles: (titles) => set({ contentControlTitles: titles }),
  setCritiquing: (critiquing) => set({ critiquing }),
  setCritiqueRuns: (runs) => set({ critiqueRuns: runs }),
  updateCritiqueRun: (personaId, patch) =>
    set((s) => ({
      critiqueRuns: s.critiqueRuns.map((run) =>
        run.personaId === personaId ? { ...run, ...patch } : run,
      ),
    })),
  reset: () =>
    set({
      personas: [],
      isLoadingPersonas: false,
      personasError: null,
      selectedPersonaId: null,
      selectedPersonaIds: [],
      selectedText: null,
      criticScope: WHOLE_DOCUMENT_SCOPE,
      contentControlTitles: [],
      critiquing: false,
      critiqueRuns: [],
    }),
}));
